import { not } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import RadioGroup from 'tio-ui/components/radio-group';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import type SessoionContextService from 'tio-employee/services/session-context';
import type { Store as StoreService } from '@warp-drive/core';
import ReInput from 'tio-ui/components/input';
import Button from 'tio-ui/components/button';
import {
  submitNonTaCoachingRequest,
  submitTasCoachingRequest,
} from 'tio-data/requests/coaching-actions';

export interface TuitionAssistanceFormsMentoringSignature {
  Args: {
    employee: EmployeeModel;
    isNonTa?: boolean;
  };
}

export default class TuitionAssistanceFormsMentoring extends Component<TuitionAssistanceFormsMentoringSignature> {
  @service declare sessionContext: SessoionContextService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  @tracked topic = '';
  @tracked previousCredentials = '';
  @tracked preferredFormat?: string;
  @tracked previousLoans?: string;
  @tracked educationType = '';
  @tracked currentlyEnrolled?: string;
  @tracked institutionCounselor?: string;
  @tracked preferredContactMethod?: string;
  @tracked submissionError = false;

  get employee() {
    return this.args.employee;
  }

  get plainText() {
    const employeeInfo = {
      firstName: this.employee.firstName,
      lastName: this.employee.lastName,
      id: this.employee.id,
      email: this.employee.email,
    };

    const answers = {
      scheduling_question_topic: this.topic,
      previous_relevant_credentials: this.previousCredentials,
      preferred_learning_format: this.preferredFormat,
      previous_loans: this.previousLoans,
      type_of_education: this.educationType,
      currently_enrolled: this.currentlyEnrolled,
      counselor_at_selected_institution: this.institutionCounselor,
      preferred_contact_method: this.preferredContactMethod,
      ...employeeInfo,
    };

    let plainText = '';

    Object.keys(answers).forEach((question) => {
      // @ts-expect-error: not sure about this one
      if (answers[question]) {
        // @ts-expect-error: not sure about this one
        plainText += ` ${question}: ${answers[question]}.` + '\n';
      }
    });
    return plainText;
  }

  get submitEnabled() {
    const fieldsPresent =
      this.topic &&
      this.previousCredentials &&
      this.preferredFormat &&
      this.previousLoans &&
      this.educationType &&
      this.currentlyEnrolled &&
      this.institutionCounselor &&
      this.preferredContactMethod;

    if (!fieldsPresent) {
      return false;
    } else {
      return true;
    }
  }

  get submitNonTaEnabled() {
    const fieldsPresent = this.topic && this.previousLoans && this.preferredContactMethod;

    if (!fieldsPresent) {
      return false;
    } else {
      return true;
    }
  }

  submitRequest = async () => {
    let ok = true;
    try {
      await this.store.request(submitTasCoachingRequest(this.plainText));
    } catch (error) {
      console.error('Failed to submit TA request:', error);
      ok = false;
    }

    this.submissionError = !ok;
    if (!ok) return;

    // If phone is preferred, open Calendly in a new tab (Safari-friendly delay)
    if (this.preferredContactMethod === 'Phone') {
      setTimeout(() => {
        const newWindow = window.open(
          'https://calendly.com/ta-mentoring',
          '_blank',
          'noopener,noreferrer'
        );
        if (newWindow) {
          newWindow.opener = null;
        }
      }, 0);
    }

    // Navigate to TA dashboard and flag success via query param
    this.router.transitionTo('authenticated.tas.dashboard', {
      queryParams: { formSubmitted: true },
    });
  };

  submitNonTaRequest = async () => {
    let ok = true;
    try {
      await this.store.request(submitNonTaCoachingRequest(this.plainText));
    } catch (error) {
      console.error('Failed to submit non-TA request:', error);
      ok = false;
    }

    this.submissionError = !ok;
    if (!ok) return;

    // Scheduling is only needed for a phone session
    if (this.preferredContactMethod === 'Phone') {
      this.router.transitionTo('authenticated.college-planning-mentors.index', {
        queryParams: { phoneScheduler: true },
      });
    } else {
      this.router.transitionTo('authenticated.dashboard', {
        queryParams: { formSubmitted: true },
      });
    }
  };

  <template>
    {{#if @isNonTa}}
      <div class="p-2 ml-4 max-w-2xl">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_1"}}</p>
        <ReInput
          data-test-id="topic-input"
          @value={{this.topic}}
          @onChange={{fn (mut this.topic)}}
          @size="lg"
          class="ml-3"
        />
      </div>
      <div class="p-2 ml-4">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_2"}}</p>
        <RadioGroup
          data-test-id="previous-loans-radio-group"
          class="ml-3"
          @value={{this.previousLoans}}
          @size="lg"
          @onChange={{fn (mut this.previousLoans)}}
          @options={{array
            (hash key="Private" label="Private" dataTestId="radio-private")
            (hash key="Federal" label="Federal" dataTestId="radio-federal")
            (hash key="Both" label="Both" dataTestId="radio-both")
            (hash
              key="I don't have student loans"
              label="I don't have student loans"
              dataTestId="radio-no-student-loans"
            )
          }}
        />
      </div>
      <div class="p-2 ml-4">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_3"}}</p>
        <RadioGroup
          data-test-id="contact-method-radio-group"
          class="ml-3"
          @value={{this.preferredContactMethod}}
          @size="lg"
          @onChange={{fn (mut this.preferredContactMethod)}}
          @options={{array
            (hash
              key="Email"
              label=(t "tuition_assistance.mentoring.send_me_an_email")
              dataTestId="radio-email"
            )
            (hash
              key="Phone"
              label=(t "tuition_assistance.mentoring.schedule_phone_call")
              dataTestId="radio-phone"
            )
          }}
        />
      </div>
    {{else}}
      <div class="p-2 ml-4 max-w-2xl">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_1"}}</p>
        <ReInput
          data-test-id="topic-input"
          @value={{this.topic}}
          @onChange={{fn (mut this.topic)}}
          @size="lg"
          class="ml-3"
        />
      </div>
      <div class="p-2 ml-4 max-w-2xl">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_2"}}</p>
        <ReInput
          data-test-id="previous-credentials-input"
          @value={{this.previousCredentials}}
          @onChange={{fn (mut this.previousCredentials)}}
          @size="lg"
          class="ml-3"
        />
      </div>
      <div class="p-2 ml-4">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_3"}}</p>
        <RadioGroup
          data-test-id="preferred-format-radio-group"
          class="ml-3"
          @value={{this.preferredFormat}}
          @size="lg"
          @onChange={{fn (mut this.preferredFormat)}}
          @options={{array
            (hash key="Online" label="Online" dataTestId="radio-online")
            (hash key="In Person" label="In Person" dataTestId="radio-in-person")
            (hash key="Hybrid" label="Hybrid" dataTestId="radio-hybrid")
          }}
        />
      </div>
      <div class="p-2 ml-4">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_4"}}</p>
        <RadioGroup
          data-test-id="previous-loans-radio-group"
          class="ml-3"
          @value={{this.previousLoans}}
          @size="lg"
          @onChange={{fn (mut this.previousLoans)}}
          @options={{array
            (hash key="Private" label="Private" dataTestId="radio-private")
            (hash key="Federal" label="Federal" dataTestId="radio-federal")
            (hash key="Both" label="Both" dataTestId="radio-both")
            (hash
              key="I don't have student loans"
              label="I don't have student loans"
              dataTestId="radio-no-student-loans"
            )
          }}
        />
      </div>
      <div class="p-2 ml-4 max-w-2xl">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_5"}}</p>
        <ReInput
          data-test-id="education-type-input"
          @value={{this.educationType}}
          @onChange={{fn (mut this.educationType)}}
          @size="lg"
          class="ml-3"
        />
      </div>
      <div class="p-2 ml-4">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_6"}}</p>
        <RadioGroup
          data-test-id="currently-enrolled-radio-group"
          class="ml-3"
          @value={{this.currentlyEnrolled}}
          @size="lg"
          @onChange={{fn (mut this.currentlyEnrolled)}}
          @options={{array
            (hash key="No" label="No" dataTestId="radio-no")
            (hash key="Yes" label="Yes" dataTestId="radio-yes")
          }}
        />
      </div>
      <div class="p-2 ml-4 max-w-2xl">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_7"}}</p>
        <ReInput
          data-test-id="institution-counselor-input"
          @value={{this.institutionCounselor}}
          @onChange={{fn (mut this.institutionCounselor)}}
          @size="lg"
          class="ml-3"
        />
      </div>
      <div class="p-2 ml-4">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_8"}}</p>
        <RadioGroup
          data-test-id="contact-method-radio-group"
          class="ml-3"
          @value={{this.preferredContactMethod}}
          @size="lg"
          @onChange={{fn (mut this.preferredContactMethod)}}
          @options={{array
            (hash
              key="Email"
              label=(t "tuition_assistance.mentoring.send_me_an_email")
              dataTestId="radio-email"
            )
            (hash
              key="Phone"
              label=(t "tuition_assistance.mentoring.schedule_phone_call")
              dataTestId="radio-phone"
            )
          }}
        />
      </div>
    {{/if}}
    <div class="tio-flex-center py-10">
      {{#if @isNonTa}}
        <Button
          @intent="primary"
          {{on "click" this.submitNonTaRequest}}
          @isDisabled={{not this.submitNonTaEnabled}}
        >
          {{t "college_planning_mentors.contact"}}
        </Button>
      {{else}}
        <Button
          @intent="primary"
          {{on "click" this.submitRequest}}
          @isDisabled={{not this.submitEnabled}}
        >
          {{t "common.submit"}}
        </Button>
      {{/if}}
    </div>
  </template>
}
